/** Shared helpers for rendering scheduled-task tool plans and prompts. */

export type PlanToolStep = {
  tool_name: string
  tool_parameters?: Record<string, any>
  execution_order?: number
  depends_on?: number | null
  output_mapping?: Record<string, string> | null
  display_description?: string | null
}

const SKIP_PARAM_KEYS = new Set([
  'output_mapping',
  'depends_on',
  'execution_order',
])

/** Strip MCP prefix and turn snake_case into a readable title. */
export function friendlyToolTitle(toolName: string | null | undefined): string {
  const raw = String(toolName || '')
    .replace(/^mcp__payhelm__/i, '')
    .replace(/^mcp__/i, '')
    .trim()
  if (!raw) return 'Tool step'
  return raw
    .replace(/_/g, ' ')
    .replace(/\b([a-z])/g, (m) => m.toUpperCase())
    .replace(/\bAds\b/g, 'Ads')
    .replace(/\bId\b/g, 'ID')
    .replace(/\bUrl\b/g, 'URL')
    .replace(/\bSku\b/g, 'SKU')
}

/** Prefer display_description; fall back to a friendly tool title. */
export function planStepPrimaryCopy(step: PlanToolStep): string {
  const desc = typeof step?.display_description === 'string' ? step.display_description.trim() : ''
  if (desc) return desc
  return friendlyToolTitle(step?.tool_name)
}

export function planStepTechnicalName(step: PlanToolStep): string {
  return String(step?.tool_name || '')
    .replace(/^mcp__payhelm__/i, '')
    .replace(/^mcp__/i, '')
    .trim()
}

export function planStepDependencyLabel(step: PlanToolStep): string | null {
  const dep = step?.depends_on
  if (dep == null || Number.isNaN(Number(dep))) return null
  const n = Number(dep)
  if (n < 1) return null
  return `Uses the results from step ${n}`
}

/** Compact list of meaningful configured inputs for review cards. */
export function summarizePlanStepInputs(
  step: PlanToolStep,
  maxItems = 4,
): Array<{ key: string; value: string }> {
  const params = step?.tool_parameters
  if (!params || typeof params !== 'object' || Array.isArray(params)) return []

  const out: Array<{ key: string; value: string }> = []
  for (const [key, value] of Object.entries(params)) {
    if (SKIP_PARAM_KEYS.has(key)) continue
    if (value === null || value === undefined || value === '') continue
    let display: string
    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        display = value.length === 1 ? String(value[0]) : `${value.length} items`
      } else {
        const json = JSON.stringify(value)
        display = json.length > 60 ? `{${Object.keys(value).join(', ')}}` : json
      }
    } else {
      display = String(value)
      if (display.length > 80) display = `${display.slice(0, 77)}…`
    }
    const label = key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (s) => s.toUpperCase())
      .trim()
    out.push({ key: label, value: display })
    if (out.length >= maxItems) break
  }
  return out
}

export function sortPlanSteps<T extends PlanToolStep>(steps: T[]): T[] {
  return [...(Array.isArray(steps) ? steps : [])].sort(
    (a, b) => (a.execution_order ?? 999) - (b.execution_order ?? 999),
  )
}

export type InstructionBlock =
  | { type: 'heading'; text: string }
  | { type: 'paragraph'; text: string }
  | {
      type: 'list'
      style: 'numbered' | 'lettered' | 'bullet'
      items: string[]
      start?: number
    }

const HEADING_BANNER = /^\s*={2,}\s*(.+?)\s*={2,}\s*$/
const HEADING_DASH = /^\s*-{3,}\s*(.+?)\s*-{3,}\s*$/
const HEADING_MD = /^\s{0,3}#{1,3}\s+(.+?)\s*$/
const HEADING_BOLD = /^\s*\*\*(.+?)\*\*\s*$/
const HEADING_COLON = /^\s*([^:]{2,80}:)\s*$/
const NUMBERED_ITEM = /^\s*(\d{1,2})[.)]\s+(.+)$/
const LETTERED_ITEM = /^\s*([A-Z])[.)]\s+(.+)$/
const BULLET_ITEM = /^\s*[-*•]\s+(.+)$/

function explodeMarkerRun(
  text: string,
  regex: RegExp,
  parseValue: (raw: string) => number,
  expectedStart: number,
): string {
  const re = new RegExp(regex.source, 'g')
  const matches: Array<{ index: number; end: number; value: number; delim: string; n: string }> = []
  let m: RegExpExecArray | null
  while ((m = re.exec(text)) !== null) {
    matches.push({
      index: m.index,
      end: m.index + m[0].length,
      value: parseValue(m[1]),
      delim: m[2],
      n: m[1],
    })
  }

  const splitAt = new Set<number>()
  let i = 0
  while (i < matches.length) {
    if (matches[i].value !== expectedStart) {
      i += 1
      continue
    }
    let j = i + 1
    let expected = expectedStart + 1
    while (j < matches.length && matches[j].value === expected) {
      j += 1
      expected += 1
    }
    if (j - i >= 2) {
      for (let k = i; k < j; k += 1) splitAt.add(k)
      i = j
      continue
    }
    i += 1
  }
  if (splitAt.size === 0) return text

  let out = ''
  let last = 0
  matches.forEach((match, idx) => {
    if (!splitAt.has(idx)) return
    out += text.slice(last, match.index)
    const atLineStart =
      match.index === 0 || text[match.index - 1] === '\n' || text[match.index] === '\n'
    out += atLineStart ? `${match.n}${match.delim} ` : `\n${match.n}${match.delim} `
    last = match.end
  })
  out += text.slice(last)
  return out
}

/** Pull banners and inline 1. / A. sequences onto their own lines. */
export function normalizeInstructionText(text: string): string {
  let next = text.replace(/\r\n/g, '\n')
  next = next.replace(/[ \t]*={3,}[ \t]*([^=\n]+?)[ \t]*={3,}[ \t]*/g, '\n\n=== $1 ===\n\n')
  next = explodeMarkerRun(next, /(?<!\d)(\d{1,2})([.)])(?=\s+\S)/, Number, 1)
  next = explodeMarkerRun(
    next,
    /(?<![A-Za-z])([A-Z])([.)])(?=\s+\S)/,
    (raw) => raw.charCodeAt(0),
    'A'.charCodeAt(0),
  )
  return next.replace(/\n{3,}/g, '\n\n').trim()
}

function listStyleForLine(
  line: string,
): { style: 'numbered' | 'lettered' | 'bullet'; item: string; start?: number } | null {
  const numbered = line.match(NUMBERED_ITEM)
  if (numbered) {
    return { style: 'numbered', item: numbered[2].trim(), start: Number(numbered[1]) }
  }
  const lettered = line.match(LETTERED_ITEM)
  if (lettered) {
    return { style: 'lettered', item: lettered[2].trim() }
  }
  const bullet = line.match(BULLET_ITEM)
  if (bullet) {
    return { style: 'bullet', item: bullet[1].trim() }
  }
  return null
}

function headingForLine(line: string): string | null {
  const banner = line.match(HEADING_BANNER) || line.match(HEADING_DASH)
  if (banner) return banner[1].trim()
  const md = line.match(HEADING_MD)
  if (md) return md[1].trim()
  const bold = line.match(HEADING_BOLD)
  if (bold) return bold[1].trim()
  if (HEADING_COLON.test(line) && !listStyleForLine(line)) {
    return line.trim()
  }
  return null
}

function flushParagraph(
  lines: string[],
  blocks: InstructionBlock[],
): void {
  const text = lines.join(' ').replace(/\s+/g, ' ').trim()
  if (text) blocks.push({ type: 'paragraph', text })
  lines.length = 0
}

/** Structured blocks for merchant-readable instruction review. */
export function parseInstructionBlocks(text: string | null | undefined): InstructionBlock[] {
  const raw = normalizeInstructionText(String(text || ''))
  if (!raw) return []

  const blocks: InstructionBlock[] = []
  const para: string[] = []
  let list: Extract<InstructionBlock, { type: 'list' }> | null = null

  const endList = () => {
    if (list && list.items.length) blocks.push(list)
    list = null
  }

  for (const line of raw.split('\n')) {
    const trimmed = line.trim()
    if (!trimmed) {
      flushParagraph(para, blocks)
      endList()
      continue
    }

    const heading = headingForLine(trimmed)
    if (heading) {
      flushParagraph(para, blocks)
      endList()
      blocks.push({ type: 'heading', text: heading.replace(/:$/, '') })
      continue
    }

    const item = listStyleForLine(trimmed)
    if (item) {
      flushParagraph(para, blocks)
      if (!list || list.style !== item.style) {
        endList()
        list = {
          type: 'list',
          style: item.style,
          items: [item.item],
          ...(item.start != null ? { start: item.start } : {}),
        }
      } else {
        list.items.push(item.item)
      }
      continue
    }

    if (list && /^\s{2,}\S/.test(line)) {
      const last = list.items.length - 1
      list.items[last] = `${list.items[last]} ${trimmed}`.replace(/\s+/g, ' ')
      continue
    }

    endList()
    para.push(trimmed)
  }

  flushParagraph(para, blocks)
  endList()
  return blocks
}

/** Split long instruction text into paragraphs for readable review. */
export function splitInstructionParagraphs(text: string | null | undefined): string[] {
  return parseInstructionBlocks(text)
    .map((block) => {
      if (block.type === 'paragraph' || block.type === 'heading') return block.text
      return block.items.join(' ')
    })
    .filter(Boolean)
}

/**
 * Decide whether instructions should start collapsed.
 * Long multi-paragraph prompts benefit from collapse by default.
 */
export function shouldCollapseInstructions(text: string | null | undefined): boolean {
  const raw = String(text || '')
  if (raw.length > 420) return true
  return parseInstructionBlocks(raw).length > 2
}
